import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { FieldsetToggleComponent } from "@components/fieldset-toggle/fieldset-toggle.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { chain } from "lodash";
import { Observable } from "rxjs";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { Anskaffningsformer } from "~app/common/models/constants";
import { ITplArbetsorder } from "../../../models/dtos";
import { FilterBaseComponent } from "../filter-base-component";
import { TplFilterContext } from "../tpl-filter-context";
import { ITplArbetsorderFilter } from "../tpl-filter.models";

@Component({
    selector: "rask-tpl-anskaffningsform-filter",
    templateUrl: "./tpl-anskaffningsform-filter.component.html",
    styleUrls: ["./tpl-anskaffningsform-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FieldsetToggleComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent
    ]
})
export class TplAnskaffningsformFilterComponent extends FilterBaseComponent implements OnInit, ITplArbetsorderFilter {

    @Input() dataSource: ITplArbetsorder[];

    options: Observable<string[]>;
    selectableAnskaffningsformer: SelectionItem[] = [];

    constructor() {
        super("anskaffningsform");
    }

    ngOnInit(): void {
        this.onFormInit(new UntypedFormControl([]));
        this.initOptions();
    }

    match(item: ITplArbetsorder): boolean {
        const selectedAnskaffningsformer = this.selectableAnskaffningsformer
            .filter(si => si.selected)
            .map(si => si.id);

        if (selectedAnskaffningsformer.includes(item.anskaffningsform)) {
            return true;
        }

        const selectedAffarsformer = chain(this.selectableAnskaffningsformer)
            .flatMap(si => si.children)
            .filter(si => !!si)
            .filter(si => si.selected)
            .map(si => si.id)
            .value();

        if (selectedAffarsformer.includes(item.affarsform)) {
            return true;
        }

        return false;
    }

    value(): string[] {
        return this.getSelectedValues().map(si => si.id);
    }

    isActive(): boolean {
        return this.value()?.length > 0;
    }

    setAnskaffningsformFromSelector(): void {
        this.filterLength = this.getSelectedValues().length;
    }

    getSelectedValues(): SelectionItem[] {
        const selectedAnskaffningsformer = this.selectableAnskaffningsformer
            .filter(si => si.selected);

        const selectedAffarsformer = chain(this.selectableAnskaffningsformer)
            .flatMap(si => si.children)
            .filter(si => !!si)
            .filter(si => si.selected)
            .value();

        return selectedAnskaffningsformer.concat(selectedAffarsformer);
    }

    protected onAddFilterToContext(filterContext: TplFilterContext) {
        filterContext.addArbetsorderFilter(this.name, this);
    }

    protected onFilterInit(state: string[]): void {
        this.control.setValue(state ?? []);
    }

    protected onFormChange(value: string[]): void {
        this.getSelectedValues().forEach(si => si.selected = false);
        this.filterLength = 0;
    }

    private initOptions(): void {
        if (this.dataSource.length === 0) {
            return;
        }

        const groupedAoAnskaffningsform = chain(this.dataSource)
            .groupBy(ao => ao.anskaffningsform)
            .value();

        const tempAnskaffningsFormer = [];
        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.Egenskog} (${groupedAoAnskaffningsform[Anskaffningsformer.Egenskog]?.length ?? 0})`,
                id: Anskaffningsformer.Egenskog,
                value: Anskaffningsformer.Egenskog
            });

        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.Virkeskop} (${groupedAoAnskaffningsform[Anskaffningsformer.Virkeskop]?.length ?? 0})`,
                value: Anskaffningsformer.Virkeskop,
                id: Anskaffningsformer.Virkeskop
            });

        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.CentralaVirkeskop} (${groupedAoAnskaffningsform[Anskaffningsformer.CentralaVirkeskop]?.length ?? 0})`,
                value: Anskaffningsformer.CentralaVirkeskop,
                id: Anskaffningsformer.CentralaVirkeskop
            });

        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.CentralaKop} (${groupedAoAnskaffningsform[Anskaffningsformer.CentralaKop]?.length ?? 0})`,
                value: Anskaffningsformer.CentralaKop,
                id: Anskaffningsformer.CentralaKop
            });
        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.EgenTerminal} (${groupedAoAnskaffningsform[Anskaffningsformer.EgenTerminal]?.length ?? 0})`,
                value: Anskaffningsformer.EgenTerminal,
                id: Anskaffningsformer.EgenTerminal
            });
        tempAnskaffningsFormer.push(
            {
                text: `${Anskaffningsformer.Import} (${groupedAoAnskaffningsform[Anskaffningsformer.Import]?.length ?? 0})`,
                value: Anskaffningsformer.Import,
                id: Anskaffningsformer.Import
            });

        this.selectableAnskaffningsformer = tempAnskaffningsFormer;
    }
}
